import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Plus,
  X,
  HeartPulse,
  Search,
  Edit2,
  Trash2,
  Eye,
  AlertTriangle,
  Flame,
  CheckCircle2,
  PauseCircle,
  Building2,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

import { PageHead, Panel, StatCard } from "@/project/components/dashboard-shell";
import { formatINR } from "@/project/data/sample";
import { useCampaigns, useOrganisations, useSetCampaignStatus } from "@/project/lib/queries";
import { downloadCsv } from "@/project/lib/receipt";

export const Route = createFileRoute("/admin/causes")({
  component: AdminCauses,
});

export function AdminCauses() {
  const { data: initialCampaigns = [], isLoading } = useCampaigns({ all: true });
  const { data: orgs = [] } = useOrganisations({ all: true });
  const setStatus = useSetCampaignStatus();

  const [localCampaigns, setLocalCampaigns] = useState<any[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingCamp, setEditingCamp] = useState<any | null>(null);
  const [viewingCamp, setViewingCamp] = useState<any | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  // Form State
  const [title, setTitle] = useState("");
  const [orgId, setOrgId] = useState("");
  const [goal, setGoal] = useState<number>(50000);
  const [urgency, setUrgency] = useState<"Standard" | "Urgent" | "Critical">("Urgent");
  const [category, setCategory] = useState("Animal Seva");

  const allCampaigns = [...localCampaigns, ...initialCampaigns];

  const filteredCampaigns = allCampaigns.filter((c) => {
    const matchesSearch =
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      orgName(c.org_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesUrgency =
      urgencyFilter === "All" || (c.urgency ?? "Standard") === urgencyFilter;
    const matchesStatus =
      statusFilter === "All" || c.status === statusFilter;
    return matchesSearch && matchesUrgency && matchesStatus;
  });

  function orgName(id: string | null) {
    return orgs.find((o) => o.id === id)?.name ?? "Shree Ahmedabad Panjrapole";
  }

  function openCreateModal() {
    setEditingCamp(null);
    setTitle("");
    setGoal(50000);
    setUrgency("Urgent");
    setCategory("Animal Seva");
    setOrgId(orgs[0]?.id || "org-1");
    setOpenModal(true);
  }

  function openEditModal(c: any) {
    setEditingCamp(c);
    setTitle(c.title);
    setGoal(c.goal || 50000);
    setUrgency(c.urgency || "Urgent");
    setCategory(c.category || "Animal Seva");
    setOrgId(c.org_id || "org-1");
    setOpenModal(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a campaign title.");
      return;
    }

    if (editingCamp) {
      setLocalCampaigns((prev) =>
        prev.map((c) =>
          c.id === editingCamp.id
            ? { ...c, title, goal, urgency, category, org_id: orgId }
            : c
        )
      );
      toast.success(`Updated campaign "${title}" successfully!`);
    } else {
      const newCamp = {
        id: `camp-${Date.now()}`,
        title,
        org_id: orgId || orgs[0]?.id || "org-1",
        goal,
        raised: 0,
        urgency,
        category,
        status: "approved",
        ends_on: new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
      };
      setLocalCampaigns([newCamp, ...localCampaigns]);
      toast.success(`Successfully launched campaign "${title}"!`);
    }

    setOpenModal(false);
  }

  async function changeStatus(id: string, status: "approved" | "rejected" | "suspended") {
    try {
      await setStatus.mutateAsync({ id, status });
      toast.success(`Campaign status updated to ${status}.`);
    } catch (e) {
      // Local fallback toggle
      setLocalCampaigns((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status } : c))
      );
      toast.success(`Updated status to ${status}.`);
    }
  }

  function handleDelete(id: string, titleStr: string) {
    if (confirm(`Remove campaign "${titleStr}"?`)) {
      setLocalCampaigns((prev) => prev.filter((c) => c.id !== id));
      toast.success(`Removed campaign.`);
    }
  }

  return (
    <>
      <PageHead
        title="Campaigns & Causes"
        subtitle="Approve, launch, edit and manage donation campaigns across all partner institutions."
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={openCreateModal}
              className="flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-[var(--maroon-deep)] transition"
            >
              <Plus className="h-4 w-4" /> Add Campaign
            </button>
            <button
              onClick={() =>
                downloadCsv("campaigns", [
                  ["Title", "Organisation", "Domain", "Goal", "Raised", "Urgency", "Status", "Ends"],
                  ...allCampaigns.map((c) => [
                    c.title,
                    orgName(c.org_id),
                    c.domain ?? "",
                    c.goal,
                    c.raised,
                    c.urgency ?? "",
                    c.status,
                    c.ends_on ?? "",
                  ]),
                ])
              }
              className="rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold hover:bg-muted transition"
            >
              Export CSV
            </button>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <StatCard label="Total Campaigns" value={String(allCampaigns.length)} tone="primary" />
        <StatCard label="Live Campaigns" value={String(allCampaigns.filter((c) => c.status === "approved").length)} tone="good" />
        <StatCard label="Pending Approval" value={String(allCampaigns.filter((c) => c.status === "pending").length)} tone="warn" />
        <StatCard label="Total Goal Value" value={formatINR(allCampaigns.reduce((s, c) => s + (c.goal || 0), 0))} />
      </div>

      <Panel
        title="All Campaigns"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-full border border-border/80 bg-white px-3 py-1 text-xs">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent outline-none w-36 sm:w-48 placeholder:text-muted-foreground"
              />
            </div>
            <select
              value={urgencyFilter}
              onChange={(e) => setUrgencyFilter(e.target.value)}
              className="rounded-full border border-border/80 bg-white px-3 py-1 text-xs font-semibold outline-none cursor-pointer"
            >
              <option value="All">All Urgencies</option>
              <option value="Critical">Critical</option>
              <option value="Urgent">Urgent</option>
              <option value="Standard">Standard</option>
            </select>
          </div>
        }
      >
        {isLoading && <p className="py-6 text-sm text-foreground/70">Loading campaigns…</p>}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/30">
              <tr className="text-left">
                <th className="py-3 px-3">Campaign Title</th>
                <th className="px-3">Organisation</th>
                <th className="px-3">Funding Progress</th>
                <th className="px-3">Urgency</th>
                <th className="px-3">Status</th>
                <th className="text-right px-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredCampaigns.map((c) => {
                const pct = Math.min(100, Math.round(((c.raised || 0) / Math.max(1, c.goal || 1)) * 100));
                return (
                  <tr key={c.id} className="hover:bg-[var(--cream-2)]/60 transition-colors">
                    <td className="py-3 px-3 max-w-xs">
                      <p className="font-bold text-[var(--maroon-deep)] leading-snug line-clamp-1">{c.title}</p>
                      <p className="text-[11px] text-muted-foreground">{c.category || "Animal Seva"}</p>
                    </td>
                    <td className="px-3 font-medium text-foreground/80">{orgName(c.org_id)}</td>
                    <td className="px-3">
                      <div className="w-36">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-extrabold text-[var(--maroon)]">{formatINR(c.raised || 0)}</span>
                          <span className="text-muted-foreground text-[10px]">{pct}%</span>
                        </div>
                        <div className="h-1.5 w-full bg-border/60 rounded-full overflow-hidden">
                          <div className="h-full bg-[var(--maroon)] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase font-extrabold ${
                          c.urgency === "Critical"
                            ? "bg-red-100 text-red-700 border border-red-200"
                            : c.urgency === "Urgent"
                            ? "bg-amber-100 text-amber-800 border border-amber-200"
                            : "bg-blue-100 text-blue-700 border border-blue-200"
                        }`}
                      >
                        {c.urgency === "Critical" && <Flame className="h-3 w-3 fill-current" />}
                        {c.urgency ?? "Standard"}
                      </span>
                    </td>
                    <td className="px-3 capitalize font-bold text-xs">
                      <span className={`inline-flex items-center gap-1 ${c.status === "approved" ? "text-emerald-700" : "text-amber-700"}`}>
                        {c.status === "approved" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <PauseCircle className="h-3.5 w-3.5 text-amber-600" />}
                        {c.status}
                      </span>
                    </td>
                    <td className="text-right px-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setViewingCamp(c)}
                          title="View Details"
                          className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-muted text-foreground/75 transition"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => openEditModal(c)}
                          title="Edit Campaign"
                          className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-muted text-[var(--maroon)] transition"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        {c.status !== "approved" ? (
                          <button
                            onClick={() => changeStatus(c.id, "approved")}
                            className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition"
                          >
                            Approve
                          </button>
                        ) : (
                          <button
                            onClick={() => changeStatus(c.id, "suspended")}
                            className="rounded-full border border-border bg-white px-3 py-1 text-xs font-semibold hover:bg-muted transition"
                          >
                            Pause
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(c.id, c.title)}
                          title="Delete"
                          className="grid h-8 w-8 place-items-center rounded-lg border border-border/60 bg-white hover:bg-red-50 text-red-600 transition"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── View Detail Modal ────────────────────────────────────────── */}
      {viewingCamp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in" onClick={() => setViewingCamp(null)}>
          <div className="w-full max-w-md rounded-3xl bg-white border border-border p-6 shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border pb-3">
              <span className="rounded-full bg-[var(--saffron)]/20 px-2.5 py-0.5 text-[10px] font-extrabold text-[var(--maroon-deep)] uppercase">
                {viewingCamp.category || "Campaign"}
              </span>
              <button onClick={() => setViewingCamp(null)} className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted text-muted-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-[var(--maroon-deep)]">{viewingCamp.title}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">by {orgName(viewingCamp.org_id)}</p>
            </div>
            <div className="rounded-2xl bg-[var(--cream-2)] p-4 space-y-2 border border-border/60">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Raised</span>
                <span className="font-extrabold text-[var(--maroon)]">{formatINR(viewingCamp.raised || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Goal Target</span>
                <span className="font-bold text-foreground">{formatINR(viewingCamp.goal || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Urgency</span>
                <span className="font-bold text-amber-700">{viewingCamp.urgency || "Standard"}</span>
              </div>
            </div>
            <div className="pt-2 flex justify-end">
              <button onClick={() => setViewingCamp(null)} className="rounded-full bg-[var(--maroon)] px-5 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)]">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add / Edit Campaign Modal ────────────────────────────────────── */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in">
          <div className="w-full max-w-lg rounded-3xl bg-white border border-border p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <HeartPulse className="h-5 w-5 text-[var(--maroon)]" />
                <h3 className="font-display text-base font-bold text-[var(--maroon-deep)]">
                  {editingCamp ? "Edit Campaign" : "Add New Campaign"}
                </h3>
              </div>
              <button
                onClick={() => setOpenModal(false)}
                className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-muted text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div>
                <label className="font-bold text-foreground mb-1 block">Campaign Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Summer Green Fodder Drive for 1,000 Gir Cows"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input-base"
                />
              </div>

              <div>
                <label className="font-bold text-foreground mb-1 block">Partner Organisation *</label>
                <select
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="input-base cursor-pointer"
                >
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.city})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-bold text-foreground mb-1 block">Funding Goal (₹) *</label>
                  <input
                    type="number"
                    min={1000}
                    required
                    value={goal}
                    onChange={(e) => setGoal(Number(e.target.value))}
                    className="input-base font-bold"
                  />
                </div>
                <div>
                  <label className="font-bold text-foreground mb-1 block">Urgency Level</label>
                  <select
                    value={urgency}
                    onChange={(e) => setUrgency(e.target.value as any)}
                    className="input-base font-bold cursor-pointer"
                  >
                    <option value="Standard">Standard</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-foreground mb-1 block">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="input-base cursor-pointer"
                >
                  <option value="Animal Seva">Animal Seva (Panjrapoles & Gaushalas)</option>
                  <option value="Bird Care">Bird ICU & Avian Care</option>
                  <option value="Medical ICU">Medical & Surgery Emergency</option>
                  <option value="Fodder Drive">Fodder & Feed Supply</option>
                </select>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpenModal(false)}
                  className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-[var(--maroon)] px-5 py-2 text-xs font-bold text-white hover:bg-[var(--maroon-deep)] shadow-xs"
                >
                  {editingCamp ? "Save Changes" : "Launch Campaign"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}